import { useState, useRef, useEffect } from "react";
import { chatbotService } from "../../services/chatbot.service";

export default function ChatbotLogic() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(true);
  const messagesEndRef = useRef(null);

  // Check API health when chatbot opens
  useEffect(() => {
    if (isOpen) {
      checkConnection();
    }
  }, [isOpen]);

  // Auto scroll to bottom when new messages are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const formatTimestamp = () => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const checkConnection = async () => {
    try {
      const isHealthy = await chatbotService.checkHealth();
      setIsConnected(isHealthy);
    } catch (error) {
      console.error("Connection check failed:", error);
      setIsConnected(false);
    }
  };

  const formatBotMessage = (text) => {
    // Convert markdown-like formatting to HTML
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // Bold with double asterisk
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')       // Bold with single asterisk
      .replace(/_(.*?)_/g, '<em>$1</em>')                 // Italic
      .replace(/`(.*?)`/g, '<code>$1</code>')             // Inline code
      .replace(/\n/g, '<br>')                             // Line breaks
      .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>'); // Code blocks
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading || !isConnected) return;

    const userMessage = {
      type: "user",
      content: inputMessage,
      timestamp: formatTimestamp(),
    };

    // Add user message to chat
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      // Call RAG Engine using /ask endpoint
      const response = await chatbotService.askQuestion(inputMessage);
      
      const botMessage = {
        type: "bot",
        content: response.answer || "Sorry, I couldn't process your request.",
        formattedContent: formatBotMessage(response.answer || "Sorry, I couldn't process your request."),
        timestamp: formatTimestamp(),
        question: response.question, // Store original question for reference
      };

      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      console.error("Chatbot error:", error);
      
      // Check if it's a connection error
      if (error.message.includes('fetch')) {
        await checkConnection();
      }
      
      const errorMessage = {
        type: "bot",
        content: "Sorry, I'm experiencing technical difficulties. Please try again later.",
        formattedContent: "Sorry, I'm experiencing technical difficulties. Please try again later.",
        timestamp: formatTimestamp(),
        isError: true,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  const setExampleQuestion = (question) => {
    setInputMessage(question);
  };

  // Add welcome message when chatbot first opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        type: "bot",
        content: "Hi! I'm your BulkHours Assistant powered by RAG Engine. I can help you with:\n\n• Project status and hours tracking\n• Time logging information\n• Project budget analysis\n• General questions about your projects\n\nTry asking about a specific project by including its Board ID (6+ digits)!",
        formattedContent: "Hi! I'm your BulkHours Assistant powered by RAG Engine. I can help you with:<br><br>• Project status and hours tracking<br>• Time logging information<br>• Project budget analysis<br>• General questions about your projects<br><br>Try asking about a specific project by including its Board ID (6+ digits)!",
        timestamp: formatTimestamp(),
        isWelcome: true,
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  return {
    isOpen,
    setIsOpen,
    messages,
    setMessages,
    inputMessage,
    setInputMessage,
    isLoading,
    setIsLoading,
    isConnected,
    setIsConnected,
    messagesEndRef,
    handleSubmit,
    clearChat,
    checkConnection,
    setExampleQuestion,
  };
}
